import { useEffect, useState } from "react";
import { api } from "./api";
import { base64UrlToUint8Array, isStandalone } from "./pwa";

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

type PushState = "loading" | "unavailable" | "ready" | "enabled" | "denied";

export function PwaControls() {
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [pushState, setPushState] = useState<PushState>("loading");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const capturePrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    const installedHandler = () => {
      setInstalled(true);
      setInstallPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", capturePrompt);
    window.addEventListener("appinstalled", installedHandler);
    return () => {
      window.removeEventListener("beforeinstallprompt", capturePrompt);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setPushState("unavailable");
      return;
    }
    void Promise.all([api.pushConfig(), navigator.serviceWorker.ready])
      .then(async ([config, registration]) => {
        if (!config.supported || !config.publicKey) {
          setPushState("unavailable");
          return;
        }
        const subscription = await registration.pushManager.getSubscription();
        setPushState(subscription ? "enabled" : Notification.permission === "denied" ? "denied" : "ready");
      })
      .catch(() => setPushState("unavailable"));
  }, []);

  const install = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === "accepted") setInstallPrompt(null);
  };

  const enablePush = async () => {
    setWorking(true);
    setError(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setPushState("denied");
        return;
      }
      const config = await api.pushConfig();
      if (!config.publicKey) throw new Error("Push notifications are not configured yet.");
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlToUint8Array(config.publicKey),
      });
      const serialized = subscription.toJSON();
      if (!serialized.endpoint || !serialized.keys?.p256dh || !serialized.keys.auth) {
        throw new Error("The browser returned an incomplete push subscription.");
      }
      await api.subscribePush({
        endpoint: serialized.endpoint,
        expirationTime: serialized.expirationTime ?? null,
        keys: { p256dh: serialized.keys.p256dh, auth: serialized.keys.auth },
      });
      setPushState("enabled");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Notifications could not be enabled.");
    } finally {
      setWorking(false);
    }
  };

  const disablePush = async () => {
    setWorking(true);
    setError(null);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await api.unsubscribePush(subscription.endpoint);
        await subscription.unsubscribe();
      }
      setPushState("ready");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Notifications could not be disabled.");
    } finally {
      setWorking(false);
    }
  };

  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <div className="rail-pwa-control">
      <button
        className={`rail-notifications${pushState === "enabled" ? " enabled" : ""}`}
        type="button"
        aria-label={pushState === "enabled" ? "Notifications enabled" : "Notification settings"}
      >
        <svg className="rail-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M18 9a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9ZM10 21h4" />
        </svg>
        {pushState === "enabled" && <i />}
      </button>
      <section className="rail-pwa-flyout" aria-label="Mobile app and notification settings">
        <strong>Notifications</strong>
        <p>{pushState === "enabled" ? "Matter activity alerts are enabled on this device." : "Get alerts for matter activity on this device."}</p>
        {!installed && installPrompt && <button onClick={() => void install()}>Install ParaPlus</button>}
        {!installed && !installPrompt && ios && <small>Install from Share → Add to Home Screen.</small>}
        {pushState === "ready" && <button disabled={working} onClick={() => void enablePush()}>Enable notifications</button>}
        {pushState === "enabled" && <button disabled={working} onClick={() => void disablePush()}>Disable notifications</button>}
        {pushState === "denied" && <small>Notifications are blocked in this device's settings.</small>}
        {pushState === "unavailable" && <small>Notifications are not available on this device yet.</small>}
        {error && <p className="mini-app-error">{error}</p>}
      </section>
    </div>
  );
}
